import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import { useAuth } from '../../components/ui/AuthStateManager';
import SuccessConfirmation from './components/SuccessConfirmation';
import PlannerSpecifications from './components/PlannerSpecifications';
import DeliveryOptions from './components/DeliveryOptions';
import SecondaryActions from './components/SecondaryActions';
import FileManagement from './components/FileManagement';
import SocialSharing from './components/SocialSharing';
import { generatePlannerPDF } from '../../utils/pdfGenerator.jsx';
import { plannerStorageService } from '../../services/plannerStorageService';
import { getSessionBackground } from '../../utils/backgroundRotation';

const PDFGenerationResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, logout } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [storageUrl, setStorageUrl] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  
  // Add background image state
  const [backgroundImage, setBackgroundImage] = useState('');

  // Load wizard data from localStorage OR navigation state (for regeneration)
  const [plannerData, setPlannerData] = useState(null);
  const [specifications, setSpecifications] = useState(null);

  useEffect(() => {
    // Check if coming from regeneration (navigation state)
    const navigationState = window.history?.state?.usr;
    
    if (navigationState?.fromRegeneration && navigationState?.plannerData) {
      // Load data from navigation state (regeneration flow)
      console.log('Loading planner data from regeneration');
      
      const regeneratedData = navigationState?.plannerData;
      const year = regeneratedData?.year || new Date()?.getFullYear();
      
      // Transform regenerated data to match expected format
      const transformedPlannerData = {
        id: navigationState?.configId || 'planner-regen',
        name: regeneratedData?.name || regeneratedData?.personalization?.name,
        thumbnailUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1806bb431-1765339180070.png",
        pageCount: 0, // Will be calculated
        fileSize: 0, // Will be estimated
        generatedAt: new Date()?.toISOString(),
        downloadUrl: null, // Already uploaded
        ...regeneratedData
      };

      // Create specifications from regenerated data
      const transformedSpecs = {
        type: "Daily Planner",
        layout: typeof regeneratedData?.layout === 'object' ? 
          Object.keys(regeneratedData?.layout)?.filter(k => regeneratedData?.layout?.[k])?.join(', ') : 
          'Standard',
        colorTheme: regeneratedData?.theme || 'Classic',
        startDate: `January 1, ${year}`,
        duration: `${year % 4 === 0 ? 366 : 365} days`,
        pageSize: regeneratedData?.size?.toUpperCase() || "LETTER",
        sections: regeneratedData?.sections || [],
        customFeatures: []
      };

      setPlannerData(transformedPlannerData);
      setSpecifications(transformedSpecs);
      
      // Auto-download after regeneration
      setTimeout(() => {
        handleDownload();
      }, 500);

    } else {
      // Original flow: Load from localStorage
      const savedData = localStorage.getItem('plannerWizardData');
      if (savedData) {
        try {
          const wizardData = JSON.parse(savedData);

          // Calculate real page count based on year
          const year = wizardData?.personalization?.year || new Date()?.getFullYear();
          const isLeapYear = year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
          const daysInYear = isLeapYear ? 366 : 365;
          const realPageCount = 1 + 1 + 12 + daysInYear; // Title + Year Overview + 12 Months + Daily Pages

          // Estimate file size (approximately 7-8KB per page for PDF with minimal graphics)
          const estimatedFileSize = realPageCount * 7500; // ~7.5KB per page average

          // Transform wizard data to planner data
          const transformedPlannerData = {
            id: searchParams?.get('id') || 'planner-001',
            name: wizardData?.personalization?.name || `My Personal Planner ${year}`,
            thumbnailUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1806bb431-1765339180070.png",
            pageCount: realPageCount,
            fileSize: estimatedFileSize,
            generatedAt: new Date()?.toISOString(),
            downloadUrl: "/downloads/my-personal-planner.pdf",
            // Keep original wizard data for PDF generation
            ...wizardData
          };

          // Create specifications from wizard data
          const transformedSpecs = {
            type: wizardData?.plannerType?.replace('-', ' ')?.replace(/\b\w/g, (l) => l?.toUpperCase()) || "Daily Planner",
            layout: wizardData?.layout?.replace('-', ' ')?.replace(/\b\w/g, (l) => l?.toUpperCase()) || "Standard",
            colorTheme: wizardData?.theme?.replace('-', ' ')?.replace(/\b\w/g, (l) => l?.toUpperCase()) || "Classic",
            startDate: `January 1, ${year}`,
            duration: `${daysInYear} days${isLeapYear ? ' (Leap Year)' : ''}`,
            pageSize: wizardData?.size?.toUpperCase() || "A4",
            sections: [
            "Daily Pages",
            "Monthly Overview",
            ...(wizardData?.sections?.includes('goals') ? ["Goal Tracking"] : []),
            ...(wizardData?.sections?.includes('notes') ? ["Notes"] : []),
            ...(wizardData?.sections?.includes('habits') ? ["Habit Tracker"] : [])],

            customFeatures: [
            wizardData?.personalization?.includeCalendar ? "Year Calendar" : null,
            wizardData?.personalization?.includeGoals ? "Goal Setting Pages" : null,
            wizardData?.personalization?.includePageNumbers ? "Page Numbers" : null,
            wizardData?.personalization?.includeIndex ? "Index" : null,
            wizardData?.personalization?.includeBottomQuotes && wizardData?.personalization?.quote ? "Inspirational Quotes" : null]?.
            filter(Boolean) // Remove null values
          };

          setPlannerData(transformedPlannerData);
          setSpecifications(transformedSpecs);
        } catch (error) {
          console.error('Error loading wizard data:', error);
          // Set minimal fallback data
          setPlannerData({
            id: 'planner-001',
            name: "My Personal Planner",
            pageCount: 379,
            fileSize: 2840000,
            generatedAt: new Date()?.toISOString()
          });
          setSpecifications({
            type: "Daily Planner",
            layout: "Standard",
            colorTheme: "Classic",
            startDate: `January 1, ${new Date()?.getFullYear()}`,
            duration: "365 days",
            pageSize: "A4",
            sections: ["Daily Pages", "Monthly Overview"],
            customFeatures: []
          });
        }
      } else {
        // No localStorage data available - use minimal defaults
        setPlannerData({
          id: 'planner-001',
          name: "My Personal Planner",
          pageCount: 379,
          fileSize: 2840000,
          generatedAt: new Date()?.toISOString()
        });
        setSpecifications({
          type: "Daily Planner",
          layout: "Standard",
          colorTheme: "Classic",
          startDate: `January 1, ${new Date()?.getFullYear()}`,
          duration: "365 days",
          pageSize: "A4",
          sections: ["Daily Pages", "Monthly Overview"],
          customFeatures: []
        });
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  // Initialize background on component mount
  useEffect(() => {
    setBackgroundImage(getSessionBackground());
  }, []);

  const handleDownload = async () => {
    if (!plannerData) {
      console.error('No planner data available');
      return;
    }

    setIsDownloading(true);
    setUploadStatus('uploading');

    try {
      // FIXED: Destructure blob from return object
      const { blob: pdfBlob } = await generatePlannerPDF({
        plannerData,
        specifications,
        includeYearView: plannerData?.personalization?.includeCalendar !== false
      });

      // Validate pdfBlob before proceeding
      if (!pdfBlob || !(pdfBlob instanceof Blob)) {
        throw new Error('PDF generation failed: Invalid blob object returned');
      }

      // Get configuration ID from search params or create new
      const configId = searchParams?.get('configId') || null;

      try {
        // Upload to Supabase storage (with retry logic)
        const uploadResult = await plannerStorageService?.uploadFullPlanner(
          pdfBlob,
          plannerData,
          configId
        );

        if (uploadResult?.success) {
          setStorageUrl(uploadResult?.storageUrl);
          setBlobUrl(uploadResult?.blobUrl);
          setUploadStatus('success');

          // Use storage URL if available, fallback to blob URL
          const downloadUrl = uploadResult?.storageUrl || uploadResult?.blobUrl;

          // Trigger download
          const link = document.createElement('a');
          link.href = downloadUrl;

          const filename = plannerData?.personalization?.name ?
          `${plannerData?.personalization?.name?.replace(/\s+/g, '-')?.toLowerCase()}-${plannerData?.personalization?.year}.pdf` :
          `planner-${plannerData?.personalization?.year || new Date()?.getFullYear()}.pdf`;

          link.download = filename;
          link.type = 'application/pdf';

          document.body?.appendChild(link);
          link?.click();
          document.body?.removeChild(link);

          // Show success message
          if (uploadResult?.uploadError) {
            alert('PDF downloaded successfully! Note: Cloud storage upload failed, but your download is ready.');
          }

        } else {
          throw new Error(uploadResult?.uploadError || 'Upload failed');
        }

      } catch (uploadError) {
        console.error('Storage upload failed:', uploadError);
        setUploadStatus('error');

        // Validate pdfBlob again before creating fallback URL
        if (!pdfBlob || !(pdfBlob instanceof Blob)) {
          throw new Error('Cannot create download: PDF generation produced invalid data');
        }

        // Fallback: Create blob URL and allow download anyway
        try {
          const fallbackUrl = URL.createObjectURL(pdfBlob);
          setBlobUrl(fallbackUrl);

          const link = document.createElement('a');
          link.href = fallbackUrl;

          const filename = plannerData?.personalization?.name ?
          `${plannerData?.personalization?.name?.replace(/\s+/g, '-')?.toLowerCase()}-${plannerData?.personalization?.year}.pdf` :
          `planner-${plannerData?.personalization?.year || new Date()?.getFullYear()}.pdf`;

          link.download = filename;
          link.type = 'application/pdf';

          document.body?.appendChild(link);
          link?.click();
          document.body?.removeChild(link);

          // Show warning
          alert('PDF downloaded successfully! However, cloud storage upload failed. Your file is available for download now.');

          // Clean up blob URL after download
          setTimeout(() => {
            try {
              URL.revokeObjectURL(fallbackUrl);
            } catch (err) {
              console.error('Error revoking object URL:', err);
            }
          }, 5000);
        } catch (urlError) {
          console.error('Failed to create object URL:', urlError);
          throw new Error('Failed to create download link: ' + urlError.message);
        }
      }

    } catch (error) {
      console.error('PDF generation failed:', error);
      setUploadStatus('error');
      
      // Provide more specific error message
      let errorMessage = 'Failed to generate PDF. ';
      if (error?.message?.includes('Invalid blob')) {
        errorMessage += 'The PDF generation process did not complete successfully. Please try again or contact support if the problem persists.';
      } else if (error?.message?.includes('object URL')) {
        errorMessage += 'Unable to create download link. Please try again.';
      } else {
        errorMessage += error?.message || 'Unknown error occurred. Please try again.';
      }
      
      alert(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEmailSend = async (email) => {
    // Simulate email sending
    await new Promise((resolve) => setTimeout(resolve, 1500));
    console.log(`Sending planner to: ${email}`);
  };

  const handleCloudSave = (provider) => {
    console.log(`Saving to ${provider}`);
    // In real app, would integrate with cloud storage APIs
  };

  const handleCreateSimilar = () => {
    // Navigate to setup wizard with pre-filled data
    navigate('/planner-setup-wizard?template=similar&id=' + plannerData?.id);
  };

  const handleCreateAccount = () => {
    navigate('/register?redirect=pdf-generation-results&id=' + plannerData?.id);
  };

  const handleShare = (platform) => {
    console.log(`Shared on ${platform}`);
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Image with Overlay - Random rotation per session */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url("${backgroundImage}")`
        }}
      >
        {/* Lighter overlay for better image visibility */}
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Content wrapper with relative positioning */}
      <div className="relative min-h-screen">
        <Header isAuthenticated={isAuthenticated} onLogout={logout} />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Loading state while data is being loaded */}
            {!plannerData || !specifications ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-pulse-gentle">
                  <div className="w-8 h-8 bg-primary rounded-full"></div>
                </div>
              </div>
            ) : (
              <>
                {/* Page Header - Update with white text and drop shadows */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center space-x-2 bg-success/90 text-white px-4 py-2 rounded-full text-sm font-medium mb-4 shadow-lg">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse-gentle"></span>
                    <span>Generation Complete</span>
                  </div>
                  <h1 className="font-heading text-3xl md:text-4xl font-bold text-white drop-shadow-lg mb-4">
                    Your Planner is Ready!
                  </h1>
                  <p className="font-body text-lg text-white/90 max-w-2xl mx-auto drop-shadow">
                    Your personalized day planner has been successfully generated with all your custom preferences. 
                    Download it now or explore additional delivery options below.
                  </p>
                </div>

                {/* Main Content Grid - Add semi-transparent white backgrounds */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Left Column - Primary Actions */}
                  <div className="lg:col-span-2 space-y-8">
                    {/* Success Confirmation */}
                    <div className="bg-white/95 border border-gray-200 rounded-lg">
                      <SuccessConfirmation
                        plannerData={plannerData}
                        onDownload={handleDownload}
                      />
                    </div>

                    {/* Planner Specifications */}
                    <div className="bg-white/95 border border-gray-200 rounded-lg">
                      <PlannerSpecifications specifications={specifications} />
                    </div>

                    {/* Delivery Options */}
                    <div className="bg-white/95 border border-gray-200 rounded-lg">
                      <DeliveryOptions
                        onEmailSend={handleEmailSend}
                        onCloudSave={handleCloudSave}
                      />
                    </div>
                  </div>

                  {/* Right Column - Secondary Actions */}
                  <div className="space-y-8">
                    {/* Secondary Actions */}
                    <div className="bg-white/95 border border-gray-200 rounded-lg">
                      <SecondaryActions
                        plannerData={plannerData}
                        onCreateSimilar={handleCreateSimilar}
                      />
                    </div>

                    {/* File Management */}
                    {!isAuthenticated && (
                      <div className="bg-white/95 border border-gray-200 rounded-lg">
                        <FileManagement
                          plannerData={plannerData}
                          onCreateAccount={handleCreateAccount}
                        />
                      </div>
                    )}

                    {/* Social Sharing */}
                    <div className="bg-white/95 border border-gray-200 rounded-lg">
                      <SocialSharing
                        plannerData={plannerData}
                        onShare={handleShare}
                      />
                    </div>
                  </div>
                </div>

                {/* Bottom Actions - Update with semi-transparent background */}
                <div className="mt-12">
                  <div className="bg-white/95 border border-gray-200 rounded-lg p-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="text-center sm:text-left">
                        <h3 className="font-heading font-semibold text-foreground mb-1">
                          Need Help?
                        </h3>
                        <p className="font-body text-sm text-muted-foreground">
                          Contact our support team if you have any questions about your planner.
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                          Contact Support
                        </button>
                        <button className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                          FAQ
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>

        {/* Download Loading Overlay - Update with upload status */}
        {isDownloading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-sm mx-4 text-center shadow-xl">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="font-heading font-semibold text-foreground mb-2">
                {uploadStatus === 'uploading' ? 'Preparing & Uploading' : 'Preparing Download'}
              </h3>
              <p className="font-body text-sm text-muted-foreground">
                {uploadStatus === 'uploading' ?'Generating PDF and uploading to cloud storage...' :'Your planner is being prepared for download...'}
              </p>
              {uploadStatus === 'error' && (
                <p className="font-body text-xs text-warning mt-2">
                  Upload failed, but download will proceed with local file
                </p>
              )}
            </div>
          </div>
        )}

        {/* Success notification for storage upload */}
        {uploadStatus === 'success' && storageUrl && (
          <div className="fixed bottom-4 right-4 bg-success text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">Planner saved to cloud storage!</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFGenerationResults;